import React, { useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { Menu } from "lucide-react";
import Sidebar from "./Sidebar";
import logo from "../../assets/svg/auth/logo.svg";
import useCustomScrollbar from "../../styles/useCustomScrollbar";
import { AuthUtils } from "../../features/auth";
import { ROUTES } from "../../routes/paths";
import "../../styles/customScrollbar.css";

/** How often the shell re-checks that the session is still inside its hours. */
const SESSION_CHECK_MS = 60_000;

/**
 * Shell for every signed-in screen. Below `md` the sidebar is an off-canvas
 * drawer opened from the mobile top bar; from `md` up it is a static rail.
 */
const AppLayout: React.FC = () => {
  const [navOpen, setNavOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Attaches the hover/scroll reveal behaviour to every
  // `.custom-scrollbar-overlay` in the tree, including ones mounted later.
  useCustomScrollbar();

  // The shell is pinned to the viewport, so the window itself must never
  // scroll — otherwise a page-level scrollbar appears alongside the shell's
  // own and drags the layout off the top. Auth screens are outside this
  // layout and keep normal document scrolling.
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  // Navigating from inside the drawer should dismiss it.
  useEffect(() => {
    setNavOpen(false);
  }, [location.pathname]);

  // `RequireAuth` checks the session when a screen is entered; this is what
  // notices it running out while someone is sitting on one. A console that
  // lists every customer should not stay open on a stale session because
  // nobody happened to navigate.
  useEffect(() => {
    const timer = window.setInterval(() => {
      if (!AuthUtils.isSessionValid()) {
        navigate(ROUTES.login, { replace: true });
      }
    }, SESSION_CHECK_MS);

    return () => window.clearInterval(timer);
  }, [navigate]);

  return (
    // `fixed inset-0` rather than `h-screen`: out of flow, the shell can never
    // add height to the document, so no descendant can push a window
    // scrollbar or leave empty page below it.
    <div className="fixed inset-0 flex overflow-hidden bg-white">
      <Sidebar open={navOpen} onClose={() => setNavOpen(false)} />

      {/* min-w-0 lets wide children (the users table) scroll inside
          themselves instead of stretching this column past the viewport.
          The off-white fill is what separates the page from the white cards
          sitting on it. */}
      <div className="flex-1 min-w-0 flex flex-col overflow-hidden bg-[#FCFCFD]">
        <div className="md:hidden flex-shrink-0 flex items-center gap-3 border-b border-line px-4 py-3">
          <button
            type="button"
            onClick={() => setNavOpen(true)}
            aria-label="Open navigation"
            aria-expanded={navOpen}
            className="p-2 -ml-2 rounded-lg text-ink-muted hover:bg-[#F4F7F8] hover:text-ink transition-colors"
          >
            <Menu className="w-5 h-5" />
          </button>
          <img src={logo} alt="Naurist" className="h-6 w-auto" />
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar-overlay">
          <Outlet />
        </div>
      </div>
    </div>
  );
};

export default AppLayout;
